import {
  Box,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { RequestProps } from '../../store/types'
import { t } from 'i18next'
import { Column, Row } from '../../store/types/utils/DataTableUtils'
import CheckboxSelectAll from '../Checkbox/CheckboxSelectAll'
import { defaultPageSize, defaultRowsPerPageOptions } from '../../config/defaultPagination'
import { StickyCell, StyledFooterCell, StyledHeaderCell, StyledStickyHeaderCell } from '../../styles/WorklogTableStyles'
import ReportPopupData from '../../store/types/utils/ReportPopupUtil'
import { Order } from '../../store/types/components/table/order'
import { StyledTableSortLabel } from '../../styles/TableStyles'
import DataTableTile from '../Tile/DataTableTile'
import SortingMenu from '../menu/SortingMenu'

const DataTable = ({
  setTableHeight,
  singleSelection,
  updateSelectedData,
  updateSelectedEmployments,
  columns,
  data,
  renderRow,
  totalElements,
  setRequestProps,
  translationForNotFound,
  page,
  setPage,
  removeCheckboxes,
  hoverOff,
  noEmptyRows,
  commonRowsPerPage,
  setCommonRowsPerPage,
  isLoading,
  unsortableColumns,
  isLastCellNormal,
  dense = true,
  localStorageRowsName,
  renderTileContent,
  renderTileCheckbox = false,
  isReportsView,
  isExpandible = false,
  renderExpandedRow,
  isRenderTileContent = true,
}: {
  setTableHeight?: (height: number) => void
  singleSelection: boolean
  updateSelectedData?: (selectedIds: ReportPopupData[]) => void
  updateSelectedEmployments?: (selectedIds: string[]) => void
  columns: Column[]
  data: Row[]
  renderRow: (row: Row, index: string) => React.ReactNode
  renderTileContent?: (row: Row, index: string, checkbox?: React.ReactNode) => React.ReactNode
  totalElements: number
  setRequestProps: React.Dispatch<React.SetStateAction<RequestProps>>
  translationForNotFound: string
  page: number
  setPage: Dispatch<SetStateAction<number>>
  removeCheckboxes?: boolean
  hoverOff?: boolean
  noEmptyRows?: boolean
  commonRowsPerPage?: number
  setCommonRowsPerPage?: React.Dispatch<React.SetStateAction<number>>
  isLoading?: boolean
  unsortableColumns?: Column[]
  isLastCellNormal?: boolean
  dense?: boolean
  localStorageRowsName?: string
  renderTileCheckbox?: boolean
  isReportsView?: boolean
  isExpandible?: boolean
  renderExpandedRow?: (row: Row, index: string) => React.ReactNode
  isRenderTileContent?: boolean
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(commonRowsPerPage || defaultPageSize)
  const [orderProperty, setOrderProperty] = useState('')
  const [order, setOrder] = useState<Order>('asc')
  const [selected, setSelected] = useState<Row[]>([])
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const isDownSm = useMediaQuery(useTheme().breakpoints.down('sm'))
  const theme = useTheme()
  const isPhoneView = useMediaQuery(theme.breakpoints.up(isReportsView ? 'lg' : 'sm'))

  const maxPage = totalElements > 0 ? Math.ceil(totalElements / rowsPerPage) - 1 : 0
  const safePage = Math.min(page, maxPage)

  useEffect(() => {
    if (totalElements > 0 && page > maxPage) {
      setPage(maxPage)
    }
  }, [maxPage, page, setPage, totalElements])

  useEffect(() => {
    setSelected([])
  }, [singleSelection])

  useEffect(() => {
    if (setCommonRowsPerPage !== undefined) {
      setCommonRowsPerPage(rowsPerPage)
    }
  }, [rowsPerPage, setCommonRowsPerPage])

  useEffect(() => {
    if (updateSelectedEmployments !== undefined) {
      const selectedIds = selected.map(singleData => singleData.id)
      updateSelectedEmployments(selectedIds)
    } else {
      const selectedData = selected.map(singleData => ({
        id: singleData.id,
        leaderIds: singleData.leader
          ? [singleData.leader.id]
          : singleData.leaders?.map((leader: { id: any }) => leader.id),
      }))
      if (updateSelectedData !== undefined) {
        updateSelectedData(selectedData)
      }
    }
  }, [selected, updateSelectedData, updateSelectedEmployments])

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value)
    setRowsPerPage(newRowsPerPage)
    if (localStorageRowsName) {
      localStorage.setItem(localStorageRowsName, newRowsPerPage.toString())
    }
    setPage(0)
  }

  const handleRequestSort = (_: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderProperty === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderProperty(property)
    let orderNumber = isAsc ? 1 : 0
    setRequestProps(prev => ({ ...prev, sortOrder: orderNumber, sortField: property }))
  }
  const handleRequestTileSort = (property: string) => {
    const isAsc = orderProperty === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderProperty(property)
    let orderNumber = isAsc ? 1 : 0
    setRequestProps(prev => ({ ...prev, sortOrder: orderNumber, sortField: property }))
  }

  const handleClick = (_: React.MouseEvent<unknown>, row: Row) => {
    const indexes = selected.map(entry => entry.id)
    const selectedIndex = indexes.indexOf(row.id)
    let newSelected: Row[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1))
    }

    setSelected(newSelected)
  }

  const handleTableRowClick = (
    row: Row,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent> | React.KeyboardEvent<HTMLTableRowElement>
  ) => {
    const target = event.target as HTMLElement
    if (!target.closest('.ignore-row-click') && !target.closest('.MuiBackdrop-root')) {
      if (selected.length > 0) {
        const selectedUser = selected[0]

        if (selectedUser.id === row.id) {
          return
        }

        setSelected([row])
      } else {
        setSelected([row])
      }
    }
  }

  useEffect(() => {
    if (setTableHeight && tableContainerRef.current) {
      setTableHeight(tableContainerRef.current.offsetHeight)
    }
  }, [setTableHeight, data])

  useEffect(() => {
    setRequestProps(prev => ({
      ...prev,
      page: page,
      size: rowsPerPage,
    }))
  }, [page, rowsPerPage, setRequestProps])

  const isSelected = (row: Row) => {
    let indexes = selected.map(entry => entry.id)
    return indexes.indexOf(row.id) !== -1
  }

  const visibleRows = useMemo(() => {
    return data.slice(0, rowsPerPage)
  }, [data, rowsPerPage])

  const emptyRows = Math.max(0, Math.min(defaultPageSize, rowsPerPage) - visibleRows.length)

  return (
    <>
      {isPhoneView || !isRenderTileContent ? (
        <TableContainer
          ref={tableContainerRef}
          sx={{
            height: 'fit-content',
            border: '1px solid',
            borderColor: 'background.elevation8',
            flex: 1,
            maxWidth: '100%',
          }}
        >
          <Table size={dense ? 'small' : 'medium'} sx={{ borderCollapse: 'separate' }}>
            <TableHead>
              <TableRow style={{ height: '50px' }}>
                {!singleSelection && !removeCheckboxes && (
                  <StyledStickyHeaderCell
                    align="center"
                    sx={{
                      borderRight: '2px solid',
                      borderRightColor: 'background.elevation8',
                      p: isDownSm ? 0 : 1,
                    }}
                    width={40}
                    aria-label="Select All"
                  >
                    <CheckboxSelectAll data={data} selected={selected} setSelected={setSelected} />
                  </StyledStickyHeaderCell>
                )}
                {columns.map((column, index) => {
                  const isLastCell = !isLastCellNormal && index === columns.length - 1
                  if (column.id === 'empty') {
                    return <StyledHeaderCell component={'td'} key={index} />
                  }

                  if (column.id === 'none') {
                    return null
                  }

                  return (
                    <React.Fragment key={column.id}>
                      {!isLastCell && (
                        <StyledHeaderCell align="left" sortDirection={orderProperty === column.id ? order : false}>
                          {unsortableColumns && unsortableColumns.some(col => col.id === column.id) ? (
                            column.label
                          ) : (
                            <StyledTableSortLabel
                              active={orderProperty === column.id}
                              direction={orderProperty === column.id ? order : 'asc'}
                              onClick={e => handleRequestSort(e, column.id)}
                            >
                              {column.label}
                              {orderProperty === column.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                              ) : null}
                            </StyledTableSortLabel>
                          )}
                        </StyledHeaderCell>
                      )}
                      {isLastCell && (
                        <StyledStickyHeaderCell
                          sx={{
                            borderLeft: '2px solid',
                            borderLeftColor: 'background.elevation8',
                            right: 0,
                            padding: '0 !important',
                          }}
                          align="center"
                          width={40}
                        >
                          {column.label}
                        </StyledStickyHeaderCell>
                      )}
                    </React.Fragment>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row)
                const labelId = `enhanced-table-checkbox-${index}`
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      hover={!hoverOff}
                      sx={{
                        backgroundColor:
                          singleSelection && isItemSelected ? 'background.elevation9' : 'background.dark',
                        '&.MuiTableRow-hover': {
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'background.elevation2',
                          },
                          ':focus': {
                            bgcolor: 'background.elevation2',
                          },
                        },
                        '&.Mui-selected': {
                          bgcolor: 'transparent',
                          '&:hover': {
                            bgcolor: 'background.elevation2',
                          },
                        },
                      }}
                      role="checkbox"
                      key={row.id}
                      onClick={event => {
                        if (singleSelection) handleTableRowClick(row, event)
                      }}
                      onKeyDown={event => {
                        if (singleSelection && (event.key === 'Enter' || event.key === ' ')) {
                          handleTableRowClick(row, event)
                        }
                      }}
                      tabIndex={singleSelection ? 0 : -1}
                    >
                      {!singleSelection && !removeCheckboxes && (
                        <StickyCell
                          align="center"
                          sx={{
                            borderRight: '2px solid',
                            p: isDownSm ? 0 : 1,
                            borderRightColor: 'background.elevation8',
                            '&:hover': {
                              cursor: 'auto',
                            },
                          }}
                        >
                          <Checkbox
                            onClick={event => handleClick(event, row)}
                            checked={isItemSelected}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                          />
                        </StickyCell>
                      )}
                      {renderRow(row, labelId)}
                    </TableRow>
                    <TableRow>{isExpandible && renderExpandedRow && renderExpandedRow(row, labelId)}</TableRow>
                  </React.Fragment>
                )
              })}
              {emptyRows > 0 && emptyRows < Math.min(rowsPerPage, defaultPageSize) && !noEmptyRows && (
                <TableRow style={{ height: 52 * emptyRows }}>
                  <TableCell colSpan={9} />
                </TableRow>
              )}
              {visibleRows.length === 0 && (
                <TableRow style={{ height: 52 * emptyRows }}>
                  <TableCell colSpan={9} sx={{ textAlign: 'center' }}>
                    {isLoading ? <CircularProgress /> : t(translationForNotFound)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter
              sx={{
                height: '50px',
                bgcolor: 'white',
                width: '100%',
                left: 0,
                bottom: 0,
                zIndex: 2,
                position: 'sticky',
              }}
            >
              <TableRow>
                <StyledFooterCell colSpan={columns.length + 1}>
                  <label
                    htmlFor={'data-table-pagination'}
                    id={'data-table-pagination-label'}
                    style={{
                      position: 'absolute',
                      width: '1px',
                      height: '1px',
                      margin: '-1px',
                      padding: 0,
                      overflow: 'hidden',
                      border: 0,
                      clip: 'rect(0, 0, 0, 0)',
                    }}
                  >
                    {t('components.table.pagination.rowsPerPage')}
                  </label>
                  <TablePagination
                    sx={{
                      position: 'sticky',
                      right: 16,
                      zIndex: 10,
                      width: 'fit-content',
                      float: 'right',
                    }}
                    component="div"
                    rowsPerPageOptions={defaultRowsPerPageOptions}
                    count={totalElements < 0 ? 0 : totalElements}
                    rowsPerPage={rowsPerPage}
                    page={safePage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t('components.table.pagination.rowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) => {
                      return `${from} - ${to} ${t('components.table.pagination.of')} ${count}`
                    }}
                    slotProps={{
                      select: {
                        inputProps: {
                          id: 'data-table-pagination',
                        },
                        'aria-labelledby': 'data-table-pagination-label',
                      },
                    }}
                  />
                </StyledFooterCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      ) : (
        <Box maxHeight="100vh">
          <SortingMenu
            singleSelection={singleSelection}
            renderCheckboxSelectAll={renderTileCheckbox}
            setSelected={setSelected}
            selectedRows={selected}
            data={data}
            unsortableColumns={unsortableColumns}
            onSortChange={(property: string) => handleRequestTileSort(property)}
            currentSortProperty={orderProperty}
            sortOrder={order}
            sortableFields={columns}
            setSortOrder={setOrder}
          />
          {data.map((row, index) => (
            <DataTableTile
              singleSelection={singleSelection}
              key={index}
              setSelected={setSelected}
              selected={selected}
              updateSelectedData={updateSelectedData}
              renderCheckbox={renderTileCheckbox}
              renderTileContent={(data: Row, labelId: string, checkbox?: React.ReactNode) =>
                isRenderTileContent && renderTileContent?.(data, labelId, checkbox)
              }
              data={row}
              index={index.toString()}
            />
          ))}
          <Box
            sx={{
              position: 'sticky',
              bottom: -25,
              zIndex: 10,
              backgroundColor: 'background.main',
            }}
          >
            <TablePagination
              sx={{
                width: 'fit-content',
                margin: '0 auto',
                overflowX: 'hidden',
                position: 'sticky',
              }}
              component="div"
              rowsPerPageOptions={defaultRowsPerPageOptions}
              count={totalElements < 0 ? 0 : totalElements}
              rowsPerPage={rowsPerPage}
              page={safePage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={''}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from} - ${to} ${t('components.table.pagination.of')} ${count}`
              }}
              slotProps={{
                select: {
                  inputProps: {
                    id: 'data-table-pagination',
                  },
                  'aria-labelledby': 'data-table-pagination-label',
                },
              }}
            />
          </Box>
        </Box>
      )}
    </>
  )
}

export default DataTable
